import React, { forwardRef, useRef, useState, useImperativeHandle, useLayoutEffect, useMemo, useEffect} from "react";
import Konva from "konva";
import type { Slot } from "@/types";
import type { PageType } from "@/utils/albumData";
import KonvaSlot from "../components/KonvaSlot";
import { Stage, Layer, Rect, Line, Image, Shape, Text, Group} from "react-konva";
import { TextElement } from "../types/TextElement";
import KonvaTextItem from '../components/KonvaTextItem';
import type { ShapeElement } from '@/types/ShapeElement'; // add this at the top
import KonvaShapeItem from './KonvaShapeItem'; // import your new shape component
import { Transformer } from "react-konva";
import type { SnapRect } from "../utils/snapUtils";
import { calculateGroupSnapping } from "../utils/snapUtils";




interface AlbumPageCanvasProps {
    albumType: string;
    page: PageType;
    selectedIds: string[];
    onSelect: (slotId: string, e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => void;
    onSlotChange: (slotId: string, newAttrs: Partial<Slot>) => void;
    onSlotDragMove?: (pos: { x: number; y: number }) => void;
    onUpdateGuides: (guides: Array<{ points: number[] }>) => void;
    onDeselectAll: () => void;
    stageWidth: number;
    stageHeight: number;
    backgroundColor: string;
    backgroundImage: HTMLImageElement | null;
    backgroundOpacity: number;
    backgroundZoom: number;
    showBackground: boolean;
    backgroundOffsetX?: number;  // new
    backgroundOffsetY?: number;  // new
    maxBackgroundOffsetX?: number; // optional, for slider limits
    maxBackgroundOffsetY?: number;
    guides?: Array<{ points: number[] }>;
    isSelectedPage?: boolean;
    texts: TextElement[];
    shapes: ShapeElement[];
    selectedTextId?: string | null;
    isEditingText?: boolean;
    onSelectText?: (id: string | null) => void;
    onTextChange?: (id: string, changes: Partial<TextElement>) => void;
    onSetEditingText?: (active: boolean) => void;
    onDragText?: (id: string, pos: { x: number; y: number; width: number; height: number }) => { x: number; y: number } | void;
    onTextDragEnd?: (id: string, pos: { x: number; y: number }) => void;
    selectedShapeId: string | null;
    onSelectShape: (id: string) => void;
    onShapeChange: (id: string, newAttrs: Partial<ShapeElement>) => void;
    showCenterLine?: boolean; // <-- new optional prop
    onContextMenu?: (e: Konva.KonvaEventObject<MouseEvent>) => void;
    updateSlot: (id: string, newProps: Partial<Slot>) => void;
    onSelectMultiple?: (ids: string[]) => void;
    onSlotRightClick?: (slotId: string, e: Konva.KonvaEventObject<MouseEvent>) => void;
    editorMode: "auto" | "manual";
    showSlotNumbers?: boolean;
    selectedSize?: { width: number; height: number; unit?: "inches" | "cm" }; // new prop
    selectedOpening: string | null;
    setSelectedOpening: React.Dispatch<React.SetStateAction<string | null>>;
    handtornMode: "square" | "circle";
    scale: number;
    hoveredSlotId: string | null;
    setHoveredSlotId: (id: string | null) => void;
    onChange: (slots: Slot[]) => void;
  
}


const AlbumPageCanvas = forwardRef<Konva.Stage | null, AlbumPageCanvasProps>(
    (
        {
            albumType,
            page,
            selectedIds,
            onSelect,
            onSlotChange,
            onSlotDragMove,
            onUpdateGuides,
            onDeselectAll,
            stageWidth,
            stageHeight,
            backgroundColor,
            backgroundImage,
            backgroundOpacity,
            backgroundZoom,
            backgroundOffsetX,
            backgroundOffsetY,
            showBackground = true,
            guides = [],
            selectedTextId,
            isEditingText,
            onSelectText,
            onTextChange,
            onSetEditingText,
            onDragText,
            isSelectedPage,
            onTextDragEnd,
            selectedShapeId,
            onSelectShape,
            onShapeChange,
            showCenterLine,
            onSlotRightClick,
            updateSlot,
            onSelectMultiple,
            editorMode,
            showSlotNumbers,
            selectedSize,
            selectedOpening,
            handtornMode,
            scale,
            hoveredSlotId,
            setHoveredSlotId,
            onChange,          
        },
        ref // TypeScript now knows this is React.Ref<Konva.Stage | null>
    ) => {
   

      //  Inside the component, adjust background calculation
        let bgImgWidth = 0;
        let bgImgHeight = 0;
        let bgImgX = 0;
        let bgImgY = 0;

        if (backgroundImage) {
            const imgAspect = backgroundImage.width / backgroundImage.height;
            const canvasAspect = stageWidth / stageHeight;

            if (imgAspect > canvasAspect) {
                bgImgHeight = stageHeight * backgroundZoom;
                bgImgWidth = bgImgHeight * imgAspect;
            } else {
                bgImgWidth = stageWidth * backgroundZoom;
                bgImgHeight = bgImgWidth / imgAspect;
            }
            // Maximum allowed offset = how much image exceeds stage, divided by 2
            const maxXOffset = Math.max(0, (bgImgWidth - stageWidth) / 2);
            const maxYOffset = Math.max(0, (bgImgHeight - stageHeight) / 2);
            // Clamp the offsets
            const clampedOffsetX = Math.max(-maxXOffset, Math.min(maxXOffset, backgroundOffsetX ?? 0));
            const clampedOffsetY = Math.max(-maxYOffset, Math.min(maxYOffset, backgroundOffsetY ?? 0));
          // Center + clamped offset
            bgImgX = (stageWidth - bgImgWidth) / 2 + clampedOffsetX;
            bgImgY = (stageHeight - bgImgHeight) / 2 + clampedOffsetY;
        }
        

        const [loadedFonts,] = useState<Record<string, boolean>>({});
        const SNAP_THRESHOLD = 10;

        // Selection box state
        const stageRef = useRef<Konva.Stage | null>(null);
        useImperativeHandle(ref, () => {
            if (!stageRef.current) return null as any;
            return stageRef.current;
        });

        const [selectionBox, setSelectionBox] = useState<{
            x: number;
            y: number;
            width: number;
            height: number;
        } | null>(null);

        const selectionStartRef = useRef<{ x: number; y: number } | null>(null);
        const [isSelecting, setIsSelecting] = useState(false);
        const NO_CENTER_LINE = ["AC", "ASC", "MONTAGE", "CANVAS", "FCANVAS", "ARCH", "PRINTS", "FMOUNTS", "HIPSTER", "HANDTORN", "FPRINTS"];


        function parseSize(size: string) {
            const [w, h] = size.split("x").map(Number);
            return { w, h };
        }

      

       //#region  Canvas

        // Convert physical wrap to pixels
        const stageW = stageWidth || 100;
        const stageH = stageHeight || 100;

        // default album size in inches
        const albumW = selectedSize?.width || 10;
        const albumH = selectedSize?.height || 10;

        const wrapInches = 4.723;

        const pxPerIn = stageW && selectedSize?.width
            ? stageWidth / selectedSize.width
            : 1; // fallback

        const wrapSidePx = wrapInches ? (wrapInches / 2) * pxPerIn : 0;
        const darkWrapPx = 0.8 * pxPerIn;


        const CanvasLabel = ({ text, x, y, rotation, fontSize }: any) => {
            const textRef = React.useRef<Konva.Text>(null);

            React.useLayoutEffect(() => {
                const node = textRef.current;
                if (!node) return;

                const width = node.width();
                const height = node.height();

                node.offsetX(width / 2);
                node.offsetY(height / 2);
            }, [text, fontSize, rotation]);

            return (
                <Text
                    ref={textRef}
                    text={text}
                    x={x}
                    y={y}
                    rotation={rotation}
                    fontSize={fontSize}
                    fill="black"
                    listening={false}
                />
            );
        };


        function CanvasWrap({
            stageWidth = 100,
            stageHeight = 100,
            wrapSidePx = 0,
            darkWrapPx = 0,
        }: {
            stageWidth: number;
            stageHeight: number;
            wrapSidePx: number;
            darkWrapPx: number;
        }) {
            const outerWraps = [
                { x: 0, y: 0, width: stageWidth, height: wrapSidePx }, // top
                { x: 0, y: stageHeight - wrapSidePx, width: stageWidth, height: wrapSidePx }, // bottom
                { x: 0, y: wrapSidePx, width: wrapSidePx, height: stageHeight - 2 * wrapSidePx }, // left
                { x: stageWidth - wrapSidePx, y: wrapSidePx, width: wrapSidePx, height: stageHeight - 2 * wrapSidePx }, // right
            ].filter(r => r.width > 0 && r.height > 0);

            const darkOverlays = [
                { x: 0, y: 0, width: stageWidth, height: darkWrapPx }, // top
                { x: 0, y: stageHeight - darkWrapPx, width: stageWidth, height: darkWrapPx }, // bottom
                { x: 0, y: darkWrapPx, width: darkWrapPx, height: stageHeight - 2 * darkWrapPx }, // left
                { x: stageWidth - darkWrapPx, y: darkWrapPx, width: darkWrapPx, height: stageHeight - 2 * darkWrapPx }, // right
            ].filter(r => r.width > 0 && r.height > 0);

            const labels = [
                {
                    text: "EDGE OF CANVAS",
                    x: stageWidth / 2,
                    y: wrapSidePx / 1.5,
                    rotation: 0,
                },
                {
                    text: "EDGE OF CANVAS",
                    x: stageWidth / 2,
                    y: stageHeight - wrapSidePx / 1.5,
                    rotation: 180,
                },
                {
                    text: "EDGE OF CANVAS",
                    x: wrapSidePx / 1.5,
                    y: stageHeight / 2,
                    rotation: -90,
                },
                {
                    text: "EDGE OF CANVAS",
                    x: stageWidth - wrapSidePx / 1.5,
                    y: stageHeight / 2,
                    rotation: 90,
                },
            ];

            return (
                <Layer listening={false}>
                    {/* Wrap areas */}
                    {outerWraps.map((rect, i) => (
                        <Rect
                            key={`wrap-${i}`}
                            {...rect}
                            fill="rgba(212,213,205,0.6)"
                            listening={false}
                        />
                    ))}

                    {/* Dark edge overlay */}
                    {darkOverlays.map((rect, i) => (
                        <Rect
                            key={`dark-${i}`}
                            {...rect}
                            fill="rgba(160,160,160,0.5)"
                            listening={false}
                        />
                    ))}

                    {/* Labels */}
                    {labels.map((lbl, i) => (
                        <CanvasLabel
                            key={i}
                            text={lbl.text}
                            x={lbl.x}
                            y={lbl.y}
                            rotation={lbl.rotation}
                            fontSize={Math.min(30, wrapSidePx / 2)}
                        />
                    ))}
                </Layer>
            );
        }

  // #endregion

       //#region ARCH
        function ArchOverlay({
            stageWidth,
            stageHeight,
            selectedOpening,
            albumWidth,
            albumHeight,
            backgroundColor = "white",
        }: {
            stageWidth: number;
            stageHeight: number;
            selectedOpening: string;
            albumWidth: number;
            albumHeight: number;
            backgroundColor?: string;
        }) {
            const { w, h } = parseSize(selectedOpening);

            const scaleX = stageWidth / albumWidth;
            const scaleY = stageHeight / albumHeight;

            const MAT_OVERLAP = 0.5;

            const visibleW = w - MAT_OVERLAP;
            const visibleH = h - MAT_OVERLAP;

            const archW = visibleW * scaleX;
            const archH = visibleH * scaleY;

            const x = (stageWidth - archW) / 2;
            const y = (stageHeight - archH) / 2;

            const r = archW / 2;
            const cx = x + archW / 2;
            const cy = y + r;

            return (
                <Layer listening={false}>
                    <Shape

                        sceneFunc={(ctx) => {

                            // FULL MAT
                            ctx.beginPath();
                            ctx.rect(0, 0, stageWidth, stageHeight);
                            ctx.fillStyle = backgroundColor;
                            ctx.fill();

                            // CUT OUT ARCH
                            ctx.globalCompositeOperation = "destination-out";

                            ctx.beginPath();
                            ctx.moveTo(x, y + archH);
                            ctx.lineTo(x, y + r);
                            ctx.arc(cx, y + r, archW / 2, Math.PI, 0, false);
                            ctx.lineTo(x + archW, y + archH);
                            ctx.closePath();
                            ctx.fill();

                            ctx.globalCompositeOperation = "source-over";
                        }}
                    />
                    <Shape
                        sceneFunc={(ctx) => {

                            ctx.beginPath();

                            ctx.moveTo(x, y + archH);
                            ctx.lineTo(x, y + r);

                            ctx.arc(cx, y + r, archW / 2, Math.PI, 0, false);

                            ctx.lineTo(x + archW, y + archH);
                            ctx.closePath();

                            // ONLY STROKE = visible inner edge
                            ctx.strokeStyle = "rgba(0,0,0,0.15)";
                            ctx.lineWidth = 6;

                            ctx.shadowColor = "rgba(0,0,0,0.25)";
                            ctx.shadowBlur = 10;
                            ctx.shadowOffsetY = 2;

                            ctx.stroke();
                        }}
                    />


                </Layer>
            );
        }
          // #endregion

       //#region Prints
        const shouldShowOverlay =
            albumType === "PRINTS" &&
            !!page.overlay?.type;

        type PrintOverlayProps = {
            type: string;
            stageWidth: number;
            stageHeight: number;
        };

        const getOrientation = (w: number, h: number) => {
            if (w > h) return "horizontal";
            if (h > w) return "vertical";
            return "square";
        };
        const overlayCache: Record<string, HTMLImageElement> = {};

        const PrintOverlay = ({
            type,
            stageWidth,
            stageHeight,
        }: PrintOverlayProps) => {
            const img = getOverlayImage(type, stageWidth, stageHeight);

            if (!img) return null;

            return (
                <Image
                    image={img}
                    x={0}
                    y={0}
                    width={stageWidth}
                    height={stageHeight}
                    listening={false}
                />
            );
        };
       

        const getOverlayImage = (
            type: string,
            stageWidth: number,
            stageHeight: number
        ): HTMLImageElement | null => {
            if (!type) return null;

            const orientation = getOrientation(stageWidth, stageHeight);
            const key = `${type}-${orientation}`;

            // return cached image instantly
            if (overlayCache[key]) {
                return overlayCache[key];
            }

            // create and cache once
            const img = new window.Image();       
            img.src = `/eda_online/assets/overlays/${key}.png`;

            overlayCache[key] = img;

            return img;
        };
     
         // #endregion

       //#region HandTorn
        function HandtornOverlay({
            slot,
            mode,
        }: {
            slot: Slot;
            mode: "square" | "circle";
        }) {
            const GAP = 10;

            const cx = slot.x + slot.width / 2;
            const cy = slot.y + slot.height / 2;

            const cutRadius = Math.min(slot.width, slot.height) / 2;
            const strokeRadius = cutRadius + GAP;

            const x = slot.x - GAP;
            const y = slot.y - GAP;
            const w = slot.width + GAP * 2;
            const h = slot.height + GAP * 2;

            return (
                <Layer listening={false}>
                    <Shape
                        sceneFunc={(ctx) => {
                            ctx.save();

                            // MAT
                            ctx.beginPath();
                            ctx.rect(0, 0, ctx.canvas.width, ctx.canvas.height);
                            ctx.fillStyle = "white";
                            ctx.fill();

                            ctx.globalCompositeOperation = "destination-out";

                            if (mode === "circle") {
                                ctx.beginPath();
                                ctx.arc(cx, cy, cutRadius, 0, Math.PI * 2);
                            } else {
                                ctx.beginPath();
                                ctx.rect(slot.x, slot.y, slot.width, slot.height);
                            }

                            ctx.fill();

                            ctx.globalCompositeOperation = "source-over";

                            // BORDER (always drawn, no component swap)
                            ctx.beginPath();

                            if (mode === "circle") {
                                ctx.arc(cx, cy, strokeRadius, 0, Math.PI * 2);
                            } else {
                                ctx.rect(x, y, w, h);
                            }

                            ctx.strokeStyle = "rgba(0,0,0,0.35)";
                            ctx.lineWidth = 2;
                            ctx.shadowColor = "rgba(0,0,0,0.25)";
                            ctx.shadowBlur = 6;
                            ctx.shadowOffsetY = 2;

                            ctx.stroke();

                            ctx.restore();
                        }}
                    />
                </Layer>
            );
        }
         // #endregion

       //#region Frames
        function FramesOverlay({
            stageWidth,
            stageHeight,
            selectedOpening,
            scale,
        }: {
            stageWidth: number;
            stageHeight: number;
            selectedOpening: string | null;
            scale: number;
        }) {
            if (!selectedOpening) return null;

            const parsed = parseSize(selectedOpening);

            if (!parsed.w || !parsed.h) return null; // safety guard

            const MAT_OVERLAP = 0.1;

            const visibleW = parsed.w - MAT_OVERLAP;
            const visibleH = parsed.h - MAT_OVERLAP;

            const pxPerUnit = stageWidth / (stageWidth / scale); // remove scale dependency
            const matW = Math.round(visibleW * pxPerUnit);
            const matH = Math.round(visibleH * pxPerUnit);

            const x = (stageWidth - matW) / 2;
            const y = (stageHeight - matH) / 2;



            return (
                <Layer listening={false}>
                    <Shape
                        sceneFunc={(ctx) => {
                            ctx.beginPath();
                            ctx.rect(0, 0, stageWidth, stageHeight);
                            ctx.fillStyle = "white";;
                            ctx.fill();

                            ctx.globalCompositeOperation = "destination-out";

                            ctx.beginPath();
                            ctx.rect(x, y, matW, matH);
                            ctx.fill();

                            ctx.globalCompositeOperation = "source-over";
                        }}
                    />

                    <Shape
                        sceneFunc={(ctx) => {
                            ctx.beginPath();
                            ctx.rect(x, y, matW, matH);

                            ctx.strokeStyle = "rgba(0,0,0,0.15)";
                            ctx.lineWidth = 6;

                            ctx.shadowColor = "rgba(0,0,0,0.25)";
                            ctx.shadowBlur = 10;
                            ctx.shadowOffsetY = 2;

                            ctx.stroke();
                        }}
                    />
                </Layer>
            );
        }
              // #endregion

        const transformerRef = useRef<Konva.Transformer | null>(null);
        const slotRefs = useRef<Record<string, Konva.Group | null>>({});

        const registerSlotRef = (id: string, node: Konva.Group | null) => {
            if (node) {
                slotRefs.current[id] = node;
            } else {
                delete slotRefs.current[id];
            }
        };


        useLayoutEffect(() => {
            const transformer = transformerRef.current;
            if (!transformer) return;

            const nodes = selectedIds
                .map(id => slotRefs.current[id])
                .filter(Boolean) as Konva.Group[];

            if (!nodes.length) {
                transformer.nodes([]);
                transformer.getLayer()?.batchDraw();
                return;
            }

            requestAnimationFrame(() => {
                transformer.nodes(nodes);
                transformer.getLayer()?.batchDraw();
            });

        }, [selectedIds, page.slots]); //  IMPORTANT

     

        const slotsById = useMemo(() => {
            const map: Record<string, Slot> = {};
            page.slots.forEach(s => {
                map[s.id] = s;
            });
            return map;
        }, [page.slots]);



        const referenceRects = useMemo(() => [
            ...page.slots.map(s => ({
                x: s.x,
                y: s.y,
                width: s.width,
                height: s.height,
            })),
            ...page.shapes.map(sh => ({
                x: sh.x,
                y: sh.y,
                width: sh.width,
                height: sh.height,
            })),
            ...page.texts.map(t => ({
                x: t.x,
                y: t.y,
                width: t.width ?? 100,
                height: (t.fontSize ?? 20) * 1.2,
            })),
        ], [page.slots, page.shapes, page.texts]);


        type TransformSnapshot = {
            x: number;
            y: number;
            width: number;
            height: number;
            rotation: number;
        };
      
        const transformSnapshotRef =
            useRef<Record<string, TransformSnapshot>>({});

     ;
       
      
        const isTransformingRef = useRef(false);

        const handleTransformEnd = () => {
            const transformer = transformerRef.current;
            if (!transformer) return;

            const nodes = transformer.nodes();
            const snapshot = transformSnapshotRef.current;

            const updatedSlots: Slot[] = [];

            nodes.forEach(node => {
                const before = snapshot[node.id()];
                if (!before) return;

                const scaleX = node.scaleX();
                const scaleY = node.scaleY();

                const newWidth = before.width * scaleX;
                const newHeight = before.height * scaleY;

                node.scaleX(1);
                node.scaleY(1);

                updatedSlots.push({
                    id: String(node.id()),
                    x: node.x(),
                    y: node.y(),
                    width: newWidth,
                    height: newHeight,

                    //  FIX 1: use snapshot, NOT node
                    rotation: node.rotation(),

                    imageRotation: before.rotation,
                });
            });

            onChange(updatedSlots);

            //  REMOVE THIS LINE:
            transformer.nodes([]);
            transformer.getLayer()?.batchDraw();
            onUpdateGuides?.([]);
        };

        const groupSnapshotRef = useRef<SnapRect[]>([]);
        const isDraggingRef = useRef(false);
      

      

        const handleGroupDragStart = () => {
            isDraggingRef.current = true;

            groupSnapshotRef.current = selectedIds
                .map(id => slotsById[id])
                .filter(Boolean)
                .map(s => ({
                    x: s.x,
                    y: s.y,
                    width: s.width,
                    height: s.height,
                }));
        };

        const handleGroupDragEnd = () => {

            const updatedSlots: Slot[] = selectedIds
                .map(id => {
                    const node = slotRefs.current[id];
                    const slot = slotsById[id];

                    if (!node || !slot) return null;

                    return {
                        ...slot,
                        x: node.x(),
                        y: node.y(),
                        rotation: node.rotation(),
                    };
                })
                .filter(Boolean) as Slot[];
          

            if (updatedSlots.length) {
                onChange(updatedSlots);
            }


            onUpdateGuides?.([]);
            isDraggingRef.current = false;
        };

        const groupBounds =
            selectedIds.length > 1
                ? transformerRef.current?.getClientRect()
                : null;

        const handleGroupDragMove = () => {
            const transformer = transformerRef.current;
            if (!transformer) return;

            const groupBounds = transformer.getClientRect();

            const { x: snappedX, y: snappedY, guides } = calculateGroupSnapping({
                groupBounds,
                stageSize: {
                    width: stageWidth,
                    height: stageHeight,
                },
                snapThreshold: SNAP_THRESHOLD,
            });


            const deltaX = snappedX - groupBounds.x;
            const deltaY = snappedY - groupBounds.y;


            if (deltaX !== 0 || deltaY !== 0) {
                transformer.nodes().forEach(node => {
                    node.x(node.x() + deltaX);
                    node.y(node.y() + deltaY);
                });

                transformer.getLayer()?.batchDraw();
            }


            onUpdateGuides?.(guides);
        };

        type SlotTooltipProps = {
            hoveredSlotId: string | null;
            page: {
                slots: Array<{
                    id: string;
                    x: number;
                    y: number;
                    width: number;
                    height: number;
                    quality?: {
                        ok?: boolean;
                        dpi?: number;
                    };
                }>;
            };
            stageRef: React.RefObject<any>;
        };

        function SlotTooltip({ hoveredSlotId, page }: SlotTooltipProps) {
            const slot = hoveredSlotId
                ? page.slots.find(s => s.id === hoveredSlotId)
                : null;

            const dpi = slot?.quality?.dpi;

            if (!slot || dpi == null) return null;

            // only show warning if BELOW 200 DPI
            if (dpi >= 200) return null;

            return (
                <div
                    style={{
                        position: "absolute",
                        left: slot.x + slot.width - 30,
                        top: slot.y + slot.height - 50,
                        background: "rgba(0,0,0,0.85)",
                        color: "white",
                        padding: "6px 10px",
                        borderRadius: 6,
                        fontSize: 11,
                        pointerEvents: "none",
                    }}
                >
                    Low quality: {Math.round(dpi)} DPI (min 200 recommended)
                </div>
            );
        }


        const filledSlots = Object.values(slotsById).filter(
            (s) => s.imageSrc && s.imageSrc !== ""
        );

    
    return (
        <div style={{ width: stageWidth, height: stageHeight, background: "#fff" }}>
          
            <Stage
                ref={stageRef}
                width={stageWidth}
                height={stageHeight}
             
                onMouseDown={(e) => {
                    //  ignore right-click so selection stays
                    if (e.evt?.button === 2) return;

                    const stage = e.target?.getStage?.();
                    if (!stage) return;

                    const clickedOnEmpty = e.target === stage || e.target.name() === "background";

                    if (clickedOnEmpty) {
                        const pos = stage.getPointerPosition();
                        if (pos) {
                            selectionStartRef.current = pos;
                            setSelectionBox({ x: pos.x, y: pos.y, width: 0, height: 0 });
                            setIsSelecting(true); //  start drag mode
                        }

                        onDeselectAll(); // clear selection at start
                        onSelectText?.(null);
                        onSetEditingText?.(false);
                    }
                }}
                onMouseMove={(e) => {
                    if (!isSelecting || !selectionStartRef.current) return;
                    const stage = e.target?.getStage?.();
                    if (!stage) return;

                    const pos = stage.getPointerPosition();
                    if (!pos) return;

                    const { x: startX, y: startY } = selectionStartRef.current;
                    setSelectionBox({
                        x: Math.min(startX, pos.x),
                        y: Math.min(startY, pos.y),
                        width: Math.abs(pos.x - startX),
                        height: Math.abs(pos.y - startY),
                    });
                }}
                onMouseUp={(e) => {
                    if (!selectionStartRef.current) return;
                    if (e.evt?.button !== 0) return; // on

                    const stage = e.target.getStage();
                    const pos = stage?.getPointerPosition();
                    if (!pos) return;

                    const { x: startX, y: startY } = selectionStartRef.current;
                    const selection = {
                        x: Math.min(startX, pos.x),
                        y: Math.min(startY, pos.y),
                        width: Math.abs(pos.x - startX),
                        height: Math.abs(pos.y - startY),
                    };

                    // --- Find all slots intersecting selection box ---
                    const selectedSlots = page.slots.filter(slot => {
                        const withinX =
                            slot.x + slot.width > selection.x &&
                            slot.x < selection.x + selection.width;
                        const withinY =
                            slot.y + slot.height > selection.y &&
                            slot.y < selection.y + selection.height;
                        return withinX && withinY;
                    });

                    // --- Update selection ---
                    if (selectedSlots.length > 0) {
                        const ids = selectedSlots.map(s => s.id);
                        if (ids.length === 1) {
                            onSelect(ids[0], e);
                        } else {
                            onSelectMultiple?.(ids); //  this calls parent
                        }
                    } else {
                        onDeselectAll();
                    }

                    // Clear selection rectangle
                    selectionStartRef.current = null;
                    setSelectionBox(null);
                }}
            >
               
                {/* Background Layer */}
                <Layer>
                    <Rect
                        name="background"
                        x={0}
                        y={0}
                        width={stageWidth}
                        height={stageHeight}
                        fill={backgroundColor}
                    />
                    {showBackground && backgroundImage && (
                        <Image
                            image={backgroundImage}  // preloaded image
                            x={bgImgX}
                            y={bgImgY}
                            width={bgImgWidth}
                            height={bgImgHeight}
                            opacity={backgroundOpacity}
                            listening={false}
                        />
                    )}
                </Layer>

               
                {/* Unified Elements Layer controlling render order */}
                <Layer>
                    {page.elements.map(({ id, type }, index) => {
                        if (type === "slot") {
                           
                            const slot = slotsById[id];
                            if (!slot) return null;

                            const photoNumber = filledSlots.findIndex(
                                (s) => s.id === slot.id
                            ) + 1;
                                           
                            return (
                                <React.Fragment key={id}>

                                <KonvaSlot
                                    key={id}   
                                    registerSlotRef={registerSlotRef}
                                    imageSrc={slot.imageSrc ?? ""}
                                    x={slot.x}
                                    y={slot.y}
                                    width={slot.width}
                                    height={slot.height}
                                    rotation={slot.rotation}                               
                                    imageScale={slot.imageScale}
                                        imageRotation={slot.imageRotation ?? 0}
                                        isTransformingRef={isTransformingRef}
                                        groupBounds={groupBounds}
                                        
                                    opacity={slot.opacity}
                                    albumType={albumType}
                                    slot={slot}
                                    slots={page.slots} 
                                    onSelect={(e) => onSelect(slot.id, e)}
                                    onChange={(newAttrs) => onSlotChange(slot.id, newAttrs)}
                                    onDragMove={(pos) => onSlotDragMove?.(pos)}
                                    onUpdateGuides={onUpdateGuides}
                                    isSelected={selectedIds.includes(slot.id)}
                                    selectedIds={selectedIds}                              
                                    onRightClick={(e) => onSlotRightClick?.(slot.id, e)}
                                 
                                        onDragStart={handleGroupDragStart}
                                        onDragEnd={handleGroupDragEnd}
                                        onGroupSnapMove={(dx, dy) => {
                                            selectedIds.forEach(id => {
                                                const node = slotRefs.current[id];

                                                if (!node) return;

                                                node.x(node.x() + dx);
                                                node.y(node.y() + dy);
                                            });
                                        }}
                                    referenceRects={[
                                        ...page.slots
                                            .filter((s) => s.id !== slot.id)
                                            .map((s) => ({
                                                x: s.x,
                                                y: s.y,
                                                width: s.width,
                                                height: s.height,
                                            })),
                                        ...page.shapes.map((sh) => ({
                                            x: sh.x,
                                            y: sh.y,
                                            width: sh.width,
                                            height: sh.height,
                                        })),
                                        ...page.texts.map((t) => ({
                                            x: t.x,
                                            y: t.y,
                                            width: t.width ?? 100,
                                            height: (t.fontSize ?? 20) * 1.2,
                                        })),
                                    ]}
                                    stageHeight={stageHeight}
                                    stageWidth={stageWidth}
                                    updateSlot={updateSlot}                               
                                    editorMode={editorMode} 
                                    setHoveredSlotId={setHoveredSlotId}                                 
                                />
                                 {/*  SLOT NUMBER */ }
                                    {(showSlotNumbers) && slot.imageSrc && (
                                        <React.Fragment>
                                            <Rect
                                                x={slot.x}
                                                y={slot.y}
                                                width={22}
                                                height={22}
                                                fill="rgba(0,0,0,0.4)"
                                                listening={false}
                                            />
                                            <Text
                                                text={`${photoNumber}`}
                                                x={slot.x}
                                                y={slot.y}
                                                width={20}
                                                height={20}
                                                fontSize={13}
                                                fontFamily="Arial"
                                                fill="white"
                                                align="center"
                                                verticalAlign="middle"
                                                listening={false}
                                            />
                                        </React.Fragment>
                                    )}
                                </React.Fragment>

                            );
                        }

                        if (type === "text") {
                            const text = page.texts.find((t) => t.id === id);
                            if (!text) return null;
                            return (
                                <KonvaTextItem
                                    key={`${id}-${loadedFonts[text.fontFamily ?? "Arial"] ? "loaded" : "loading"}`}
                                    textElement={text}
                                    isSelected={selectedTextId === id}
                                    isEditing={Boolean(isEditingText) && selectedTextId === id}
                                    onSelect={() => onSelectText?.(id)}
                                    onTextChange={(id, changes) => onTextChange?.(id, changes)}
                                    onSetEditingText={(val) => onSetEditingText?.(val)}
                                    onDragMove={(id, pos) => onDragText?.(id, pos)}
                                    onDragEnd={(id, pos) => onTextDragEnd?.(id, pos)}
                                />
                            );
                        }

                        if (type === "shape") {
                            const shape = page.shapes.find((sh) => sh.id === id);
                            if (!shape) return null;
                            return (
                                <KonvaShapeItem
                                    key={shape.id}
                                    shape={shape}
                                    isSelected={selectedShapeId === shape.id}
                                    onSelect={() => onSelectShape(shape.id)}
                                    onChange={(newAttrs) => onShapeChange(shape.id, newAttrs)}
                                    referenceRects={[
                                        ...page.slots.map((s) => ({
                                            x: s.x,
                                            y: s.y,
                                            width: s.width,
                                            height: s.height,
                                        })),
                                        ...page.texts.map((t) => ({
                                            x: t.x,
                                            y: t.y,
                                            width: t.width ?? 100,
                                            height: (t.fontSize ?? 20) * 1.2,
                                        })),
                                        ...page.shapes
                                            .filter((sh) => sh.id !== shape.id)
                                            .map((sh) => ({
                                                x: sh.x,
                                                y: sh.y,
                                                width: sh.width,
                                                height: sh.height,
                                            })),
                                    ]}
                                    stageWidth={stageWidth}
                                    stageHeight={stageHeight}
                                    onUpdateGuides={onUpdateGuides}   // <-- add this line
                                />
                            );
                        }
                        return null;

                    })}
                    
                  </Layer>
                  <Layer>
                      <Transformer
                          ref={transformerRef}                         
                          // selection / UX

                          onDragMove={(e) => {
                              if (selectedIds.length > 1) {
                                  handleGroupDragMove();
                              }
                          }}
                          onDragEnd={() => {
                              if (selectedIds.length > 1) {
                                  handleGroupDragEnd();
                              }
                          }}
                          onTransformStart={() => {
                              isTransformingRef.current = true;

                              const snapshot: Record<string, TransformSnapshot> = {};

                              selectedIds.forEach(id => {
                                  const node = slotRefs.current[id];
                                  if (!node) return;

                                  snapshot[id] = {
                                      x: node.x(),
                                      y: node.y(),
                                      width: node.width() * node.scaleX(),
                                      height: node.height() * node.scaleY(),
                                      rotation: node.rotation(),
                                  };
                              });

                              transformSnapshotRef.current = snapshot;
                          }}
                          onTransformEnd={() => {
                              handleTransformEnd();
                              isTransformingRef.current = false;
                          }}
                          rotateEnabled={selectedIds.length === 1 && editorMode !== "auto"}
                          enabledAnchors={
                              editorMode === "auto"
                                  ? []
                                  : [
                                      "top-left",
                                      "top-right",
                                      "bottom-left",
                                      "bottom-right",
                                      "middle-left",
                                      "middle-right",
                                      "top-center",
                                      "bottom-center",
                                  ]
                          }

                          borderStroke="#59b2d8"
                          borderStrokeWidth={2}
                          anchorSize={12}
                          anchorStroke="#59b2d8"
                          anchorStrokeWidth={2}
                          anchorCornerRadius={2}
                          anchorFill="#fff"
                          rotateAnchorOffset={35}
                          rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
                          rotationSnapTolerance={5}
                          boundBoxFunc={(oldBox, newBox) => {
                              return newBox;
                          }}

                          anchorStyleFunc={(anchor) => {
                              if (anchor.hasName("rotater")) {
                                  anchor.scale({ x: 1.3, y: 1.3 });
                                  anchor.cornerRadius(50);
                                  anchor.strokeWidth(1.5);
                                  anchor.stroke("#59b2d8");
                              }
                          }}

                          anchorDragBoundFunc={(oldPos, newPos) => {
                              const SNAP_DISTANCE = 10;
                              const guides: { points: number[] }[] = [];

                              const otherRects = referenceRects.map(r => ({
                                  left: r.x,
                                  right: r.x + r.width,
                                  top: r.y,
                                  bottom: r.y + r.height
                              }));

                              let snappedX = newPos.x;
                              let snappedY = newPos.y;
                              let snapped = false;

                              // X snap
                              for (let rect of otherRects) {
                                  if (Math.abs(newPos.x - rect.left) < SNAP_DISTANCE) {
                                      snappedX = rect.left;
                                      guides.push({ points: [rect.left, 0, rect.left, stageHeight] });
                                      snapped = true;
                                      break;
                                  }
                                  if (Math.abs(newPos.x - rect.right) < SNAP_DISTANCE) {
                                      snappedX = rect.right;
                                      guides.push({ points: [rect.right, 0, rect.right, stageHeight] });
                                      snapped = true;
                                      break;
                                  }
                              }

                              // Y snap
                              for (let rect of otherRects) {
                                  if (Math.abs(newPos.y - rect.top) < SNAP_DISTANCE) {
                                      snappedY = rect.top;
                                      guides.push({ points: [0, rect.top, stageWidth, rect.top] });
                                      snapped = true;
                                      break;
                                  }
                                  if (Math.abs(newPos.y - rect.bottom) < SNAP_DISTANCE) {
                                      snappedY = rect.bottom;
                                      guides.push({ points: [0, rect.bottom, stageWidth, rect.bottom] });
                                      snapped = true;
                                      break;
                                  }
                              }

                            onUpdateGuides?.(snapped ? guides : []);
                            return { ...newPos, x: snappedX, y: snappedY };
                        }}
                    />
                </Layer>

                {/* draw canvas overlay */}
                {(albumType === "CANVAS" || albumType === "FCANVAS") &&
                    stageWidth > 0 &&
                    stageHeight > 0 &&
                    wrapSidePx > 0 &&
                    darkWrapPx > 0 && (
                        <CanvasWrap
                            stageWidth={stageWidth}
                            stageHeight={stageHeight}
                            wrapSidePx={wrapSidePx}
                            darkWrapPx={darkWrapPx}
                        />
                    )}

                {albumType === "ARCH" && selectedSize && (
                    <ArchOverlay
                        stageWidth={stageWidth}
                        stageHeight={stageHeight}
                        selectedOpening={selectedOpening ?? "7x5"}
                        albumWidth={selectedSize.width}
                        albumHeight={selectedSize.height}
                    />
                )}

                {(albumType === "FMOUNTS" || albumType === "FPRINTS") && (
                    <FramesOverlay
                        stageWidth={stageWidth}
                        stageHeight={stageHeight}
                        selectedOpening={selectedOpening}
                        scale={scale}
                    />
                )}
    
                {albumType === "PRINTS" && shouldShowOverlay && (                  
                    <Layer listening={false}>
                        <PrintOverlay
                            type={page.overlay!.type}
                            stageWidth={stageWidth}
                            stageHeight={stageHeight}
                        />
                    </Layer>
                )}     

                {albumType === "HANDTORN" &&
                    page.slots.map(slot => (
                        <HandtornOverlay
                            key={slot.id}
                            slot={slot}
                            mode={handtornMode}
                        />
                    ))
                }
         
                {/* Highlight border for selected page (only for normal albums, not covers) */}
                {isSelectedPage && albumType !== "AC" && albumType !== "ASC" && 
                    <Layer>
                        <Rect
                            x={1}
                            y={1}
                            width={stageWidth - 2}
                            height={stageHeight - 2}
                            stroke="#59b2d8"
                            strokeWidth={3}
                            listening={false}
                        />
                    </Layer>
                }
                {/* Guides */}
                {isSelectedPage && (
                    <Layer>
                        {guides.map((guide, i) => {
                            const [x1, y1, x2, y2] = guide.points;
                            const isVertical = x1 === x2;
                            const isHorizontal = y1 === y2;
                            const isVerticalCenter =
                                isVertical && Math.abs(x1 - stageWidth / 2) < 1;

                            const isHorizontalCenter =
                                isHorizontal && Math.abs(y1 - stageHeight / 2) < 1;
                            const isCenterGuide = isVerticalCenter || isHorizontalCenter;

                            return (
                                <Line
                                    key={i}
                                    points={guide.points}
                                    stroke={isCenterGuide ? "#ff6c6c" : "#66b8ff"}
                                    dash={isCenterGuide ? [4, 4] : [4, 4]}
                                />
                            );
                        })}
                    </Layer>
                )}
            
                {/* Center fold line layer */}
                {showCenterLine && !NO_CENTER_LINE.includes(albumType) && (
                    <Layer>
                        <Line
                            points={[stageWidth / 2, 0, stageWidth / 2, stageHeight]}
                            stroke="#ddd"
                            strokeWidth={1}
                            listening={false}
                        />
                    </Layer>
                )}
                {isSelecting && selectionBox && (
                    <Layer listening={false}>
                        <Rect
                            x={selectionBox.x}
                            y={selectionBox.y}
                            width={selectionBox.width}
                            height={selectionBox.height}
                            stroke="#4A90E2"
                            strokeWidth={1}
                            dash={[4, 4]}
                            fill="rgba(74,144,226,0.1)"
                        />
                    </Layer>
                )}
          
            </Stage>

      
            <SlotTooltip
                hoveredSlotId={hoveredSlotId}            
                page={page}
                stageRef={stageRef}
            />
           
        </div>
    );
    });

export default AlbumPageCanvas;
